/**
 * Filterable Collapsible Content Web Component
 * Provides category-based filtering for collapsible content sections
 */
if (!customElements.get('filterable-collapsible')) {
  class FilterableCollapsible extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.filtersContainer = this.querySelector('.filterable-collapsible__filters');
      this.filterList = this.querySelector('.filterable-collapsible__filter-list');
      this.contentContainer = this.querySelector('.filterable-collapsible__content');

      if (!this.contentContainer) return;

      this.tabs = [];
      this.init();
    }

    init() {
      // Build categories from DOM structure
      this.categories = this.buildCategories();

      // Only show filters if more than one category
      if (this.categories.length > 1) {
        this.generateFilterButtons();
        this.filtersContainer.style.display = '';

        // Get tabs array after generation
        this.tabs = Array.from(this.filterList.querySelectorAll('.tab'));

        this.boundOnTabSelected = this.onTabSelected.bind(this);
        this.boundOnKeydown = this.onKeydown.bind(this);
        this.addEventListener('click', this.boundOnTabSelected);
        this.addEventListener('keydown', this.boundOnKeydown);

        // Activate the first category
        if (this.tabs[0]) {
          this.switchTab(this.tabs[0]);
        }
      }
    }

    buildCategories() {
      const categories = [];
      let currentCategory = null;
      let categoryIndex = 0;

      // Get all direct children of the content container
      const children = this.contentContainer.children;

      for (let i = 0; i < children.length; i++) {
        const child = children[i];

        // Check if this is a title block (h3 with collapsible-content-title-block class)
        const isTitle = child.classList.contains('collapsible-content-title-block') ||
                       child.tagName === 'H3';

        if (isTitle) {
          // Extract title text
          const titleText = child.textContent.trim();
          // Use index-based slug to ensure uniqueness
          const slug = 'category-' + categoryIndex;
          categoryIndex++;

          currentCategory = { name: titleText, slug: slug };
          categories.push(currentCategory);

          // Assign a category to the title element
          child.dataset.category = slug;
        } else if (currentCategory) {
          // This is an item block - assign current category
          child.dataset.category = currentCategory.slug;
        }
      }

      return categories;
    }

    generateFilterButtons() {
      this.filterList.innerHTML = '';

      this.categories.forEach((category, index) => {
        const li = document.createElement('li');
        li.className = 'filterable-collapsible__filter-item';

        const button = document.createElement('button');
        button.className = 'filterable-collapsible__filter-button tab focus-inset uppercase';
        button.setAttribute('role', 'tab');
        button.setAttribute('tabindex', index === 0 ? '0' : '-1');
        button.setAttribute('data-category', category.slug);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.textContent = category.name;

        li.appendChild(button);
        this.filterList.appendChild(li);
      });
    }

    /**
     * Handle tab selection (click)
     */
    onTabSelected(event) {
      const tab = event.target.closest('.tab');
      if (!tab) return;

      this.switchTab(tab);
    }

    /**
     * Handle keyboard navigation (following theme's TabPanel pattern)
     */
    onKeydown(event) {
      const tab = event.target.closest('.tab');
      if (!tab) return;

      const key = event.code ? event.code.toUpperCase() : '';

      if (key === 'ENTER' || key === 'SPACE') {
        this.switchTab(tab);
        event.preventDefault();
        return;
      }

      const idx = this.tabs.indexOf(tab);

      if (key === 'ARROWRIGHT' || key === 'ARROWDOWN') {
        event.preventDefault();
        const next = this.tabs[(idx + 1) % this.tabs.length];
        this.switchTab(next);
        next.focus({ preventScroll: true });
      } else if (key === 'ARROWLEFT' || key === 'ARROWUP') {
        event.preventDefault();
        const prev = this.tabs[(idx - 1 + this.tabs.length) % this.tabs.length];
        this.switchTab(prev);
        prev.focus({ preventScroll: true });
      } else if (key === 'HOME') {
        event.preventDefault();
        const first = this.tabs[0];
        this.switchTab(first);
        first.focus({ preventScroll: true });
      } else if (key === 'END') {
        event.preventDefault();
        const last = this.tabs[this.tabs.length - 1];
        this.switchTab(last);
        last.focus({ preventScroll: true });
      }
    }

    /**
     * Switch to a tab and filter content
     */
    switchTab(tab) {
      if (!tab) return;

      const category = tab.dataset.category;
      if (!category) return;

      // Skip if already selected (prevents flickering in design mode)
      this.isDesignMode = window.Shopify && window.Shopify.designMode;
      if (this.isDesignMode) {
        if (tab.getAttribute('aria-selected') === 'true') return;
      }

      // Update tab states (following theme pattern)
      this.querySelectorAll('.tab[aria-selected="true"]').forEach((el) => {
        el.setAttribute('aria-selected', 'false');
        el.setAttribute('tabindex', '-1');
      });

      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('tabindex', '0');

      // Filter content by category
      this.filterByCategory(category);

      // Dispatch custom event for consistency with theme
      tab.dispatchEvent(
        new CustomEvent('item-active', {
          bubbles: true,
          detail: {
            element: tab,
            category: category,
          },
        })
      );
    }

    /**
     * Filter content items by category
     */
    filterByCategory(category) {
      const items = this.contentContainer.querySelectorAll('[data-category]');

      items.forEach((item) => {
        const itemCategory = item.dataset.category;
        const shouldShow = itemCategory === category;

        item.classList.toggle('filterable-collapsible--hidden', !shouldShow);
        item.setAttribute('aria-hidden', String(!shouldShow));
      });
    }

    /**
     * Reveal a specific category (used by Theme Customizer block selection)
     * @param {string} category - The category slug to reveal
     */
    revealBlockCategory(category) {
      if (!category || !this.tabs.length) return;

      // Find the tab button for this category
      const targetTab = this.tabs.find(
        (tab) => tab.dataset.category === category
      );

      if (targetTab) {
        this.switchTab(targetTab);
      }
    }
  }

  customElements.define('filterable-collapsible', FilterableCollapsible);
}
